import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { Chart, ArcElement, BarElement, CategoryScale, LinearScale, Legend, Tooltip } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import moment from 'moment';

import {
  showMiDetailsModal,
  hideMiDetailsModal,
  resetMiDashboard,
  getMiDashboardXls,
} from 'actions';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

class MIResults extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    clients: PropTypes.arrayOf(PropTypes.shape()),
    data: PropTypes.shape(),
    trendAnalysisType: PropTypes.string.isRequired,
    isGettingMiDashboardXls: PropTypes.bool.isRequired
  };

  static defaultProps = {
    data: null,
    clients: null
  };

  static getColourArray(arraySize) {
    const colors = [];
    const palette = Chart.defaults.backgroundColor;
    for (let i = 0; i < arraySize; i += 1) {
      const color = palette[i];
      colors.push(color);
    }
    return colors;
  }

  static getGridInterval(maxDataPoint) {
    if (maxDataPoint <= 100) {
      return 5;
    }
    if (maxDataPoint <= 1000) {
      return 50;
    }
    return Math.round(Math.ceil(maxDataPoint / 5) / 10) * 10;
  }

  /* eslint-disable no-underscore-dangle */
  static toggleDataSeries(instance, dataset, dataSeriesLabel) {
    if (!instance || !dataset) {
      return;
    }

    const isHiddenNow = dataset.hidden != null && dataset.hidden;
    const datasetLength = instance._metasets.length;
    dataset.hidden = !isHiddenNow;

    if (datasetLength > 0) {
      for (let i = 2; i < datasetLength; i += 1) {
        const currentSet = instance._metasets[i];
        if (currentSet && currentSet.label === dataSeriesLabel) {
          currentSet.hidden = !isHiddenNow;
        }
      }
    }

    instance.update();
  }
  /* eslint-enable no-underscore-dangle */

  stackedChart = null;

  pieChart = null;

  trendChart = null;

  constructor(props) {
    super(props);

    this.openMiDetailsModal = this.openMiDetailsModal.bind(this);
    this.closeMiDetailsModal = this.closeMiDetailsModal.bind(this);

    Chart.defaults.backgroundColor = ['#8DB4E2', '#B8CCE4', '#E6B8B7', '#D8E4BC', '#CCC0DA',
      '#B7DEE8', '#FCD5B4', '#538DD5', '#95B3D7', '#DA9694',
      '#C4D79B', '#B1A0C7', '#92CDDC', '#FABF8F', '#16365C',
      '#366092', '#963634', '#76933C', '#60497A', '#31869B',
      '#E26B0A', '#00B0F0', '#0070C0', '#C00000', '#92D050',
      '#00B050', '#7030A0', '#FFC000', '#0F243E', '#244062',
      '#632523', '#4F6228', '#403151', '#215967', '#974706',
      '#FF0000', '#002060'
    ];

    Chart.defaults.borderColor = Chart.defaults.backgroundColor;

    this.state = {
      graphOptions: {
        colors: {
          valid: '#26bc6a',
          invalid: '#ff2626',
          inProgress: '#8DB4E2',
          axesLabels: '#4c4c4c'
        }
      },
      filter: {
        formType: null
      },
      showValidInvalidHint: false
    };
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(resetMiDashboard());
  }

  /* eslint-disable no-underscore-dangle */
  onStackedBarLegendClick(event, elem) {
    const instance = this.stackedChart;
    if (!instance) {
      return;
    }

    const dataset = instance._metasets[elem.datasetIndex];
    if (!dataset) {
      return;
    }

    MIResults.toggleDataSeries(instance, dataset, elem.text);
  }

  onTrendBarLegendClick(event, elem) {
    const instance = this.trendChart;
    if (!instance) {
      return;
    }

    const dataset = instance._metasets[elem.datasetIndex];
    if (!dataset) {
      return;
    }

    MIResults.toggleDataSeries(instance, dataset, elem.text);
  }

  onPieChartSliceClick(event, elem) {
    if (elem && elem[0] && elem[0].index >= 0) {
      this.pieChart._metasets.forEach((dataset) => {
        dataset.data.forEach((dataItem, i) => {
          if (dataset.data[i]) {
            dataset.data[i].active = false;
          }
        });
      });
      this.setState({ filter: {
        formType: this.pieChart.boxes[0].legendItems[elem[0].index].text
      } });
    }
  }
  /* eslint-enable no-underscore-dangle */

  onDownloadExcelReportClick() {
    const { dispatch, data } = this.props;
    dispatch(getMiDashboardXls(data.clientId, data.from, data.to, data.trendAnalysis, data.includeDeletedForms));
  }

  getPieChartData() {
    const { data } = this.props;
    const { filter } = this.state;

    let labels = data != null && data.formTypes != null && data.formTypes.map(a => a != null && a.description);
    let dataPoints = data != null && data.formTypes != null && data.formTypes.map(a => a != null && a.total);

    if (filter != null && filter.formType != null) {
      labels = labels != null && labels.filter(a => a === filter.formType);
      dataPoints = data != null && data.formTypes != null && data.formTypes.filter(a => a.description === filter.formType).map(a => a != null && a.total);
    }

    const graphData = {
      labels,
      datasets: [{
        data: dataPoints,
        backgroundColor: MIResults.getColourArray(dataPoints.length)
      }]
    };
    return graphData;
  }

  getStackedBarData() {
    const { data } = this.props;
    const { graphOptions, filter } = this.state;

    let labels = data != null && data.formTypes != null && data.formTypes.map(a => a.description);
    let dataValidPoints = data != null && data.formTypes != null && data.formTypes.map(a => a.valid);
    let dataInvalidOrExpiredPoints = data != null && data.formTypes != null && data.formTypes.map(a => a.invalid || a.expired);

    if (filter != null && filter.formType != null) {
      labels = labels != null && labels.filter(a => a === filter.formType);
      dataValidPoints = data != null && data.formTypes != null && data.formTypes.filter(a => a.description === filter.formType)
        .map(a => a != null && a.valid);
      dataInvalidOrExpiredPoints = data != null && data.formTypes != null && data.formTypes.filter(a => a.description === filter.formType)
        .map(a => a != null && (a.invalid || a.expired));
    }

    const graphData = {
      labels,
      datasets: [
        {
          label: 'Valid (Non Expired)',
          backgroundColor: graphOptions.colors.valid,
          borderColor: graphOptions.colors.valid,
          borderWidth: 1,
          data: dataValidPoints
        },
        {
          label: 'Invalid/Expired',
          backgroundColor: graphOptions.colors.invalid,
          borderColor: graphOptions.colors.invalid,
          borderWidth: 1,
          data: dataInvalidOrExpiredPoints
        }
      ]
    };
    return graphData;
  }

  getTrendAnalysisData() {
    const { data } = this.props;
    const { graphOptions, filter } = this.state;

    const labels = [];
    const dates = [];
    const dataset = [];

    // Gets Trend Date - Equates to Stacks
    if (data != null && data.formTypes != null && data.formTypes.length > 0) {
      for (let f = 0; f < data.formTypes.length; f += 1) {
        const form = data.formTypes[f];

        if (!filter || !filter.formType || filter.formType === form.description) {
          if (form.trendData != null) {
            for (let t = 0; t < form.trendData.length; t += 1) {
              const trend = form.trendData[t];
              const { label } = trend;
              if (!dates.includes(label)) {
                dates.push(label);
              }
              if (!labels.includes(`${ label };${ form.description }`)) {
                labels.push(`${ label };${ form.description }`);
              }
            }
          }
        }
      }
    }

    const valid = {
      label: 'Valid (Non Expired)',
      backgroundColor: graphOptions.colors.valid,
      borderColor: graphOptions.colors.valid,
      borderWidth: 1,
      data: Array(labels.length).fill(0)
    };
    const invalid = {
      label: 'Invalid/Expired',
      backgroundColor: graphOptions.colors.invalid,
      borderColor: graphOptions.colors.invalid,
      borderWidth: 1,
      data: Array(labels.length).fill(0)
    };
    const inProgress = {
      label: 'In Progress',
      backgroundColor: graphOptions.colors.inProgress,
      borderColor: graphOptions.colors.inProgress,
      borderWidth: 1,
      data: Array(labels.length).fill(0)
    };

    if (data != null && data.formTypes != null && data.formTypes.length > 0) {
      for (let f = 0; f < data.formTypes.length; f += 1) {
        const form = data.formTypes[f];
        if (!filter || !filter.formType || filter.formType === form.description) {
          if (form.trendData != null) {
            for (let t = 0; t < form.trendData.length; t += 1) {
              const trend = form.trendData[t];
              const { label } = trend;
              if (labels.includes(`${ label };${ form.description }`)) {
                if (trend.currentStage && trend.currentStage === 'Complete') {
                  if (trend.valid && !trend.expired) {
                    valid.data[labels.indexOf(`${ label };${ form.description }`)] += trend.total;
                  } else {
                    invalid.data[labels.indexOf(`${ label };${ form.description }`)] += trend.total;
                  }
                } else {
                  inProgress.data[labels.indexOf(`${ label };${ form.description }`)] += trend.total;
                }
              }
            }
          }
        }
      }
    }

    // If changing the number of total datasets make sure to update the generateLabels() in order to update the items in the legend too
    dataset.push(valid);
    dataset.push(invalid);
    dataset.push(inProgress);

    const graphData = {
      labels,
      datasets: dataset
    };

    return graphData;
  }

  getChartHolderStyle(baseClass) {
    const { trendAnalysisType } = this.props;
    return `${ baseClass } ${ (!trendAnalysisType || trendAnalysisType === 'none') ? 'full-height' : '' }`;
  }

  getSelectedClientName() {
    const { clients, data } = this.props;

    if (clients && clients.length > 0) {
      for (let c = 0; c < clients.length; c += 1) {
        if (clients[c].key === data.clientId) {
          return clients[c].value;
        }
      }
    }
    return '';
  }

  hasData() {
    const { data } = this.props;
    return data != null && data.formTypes != null && data.formTypes.length > 0;
  }

  hasTrendData() {
    const { data } = this.props;
    if (data != null && data.formTypes != null && data.formTypes.length > 0) {
      for (let f = 0; f < data.formTypes.length; f += 1) {
        const form = data.formTypes[f];
        if (form.trendData != null && form.trendData.length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  openMiDetailsModal() {
    const { dispatch } = this.props;
    dispatch(showMiDetailsModal());
  }

  closeMiDetailsModal() {
    const { dispatch } = this.props;
    dispatch(hideMiDetailsModal());
  }

  resetDashboard() {
    const { dispatch } = this.props;
    dispatch(resetMiDashboard());
  }

  /* eslint-disable no-underscore-dangle */
  resetFilter() {
    this.setState({ filter: { formType: null } });
    this.pieChart._metasets.forEach((dataset) => {
      dataset.data.forEach((dataItem, i) => {
        if (dataset.data[i]) {
          dataset.data[i].active = true;
        }
      });
    });
  }
  /* eslint-enable no-underscore-dangle */

  render() {
    const { clients, trendAnalysisType, isGettingMiDashboardXls, data } = this.props;
    const { graphOptions, filter, showValidInvalidHint } = this.state;

    const stackedData = this.getStackedBarData();
    const maxStackedDataPoint = stackedData != null && stackedData.datasets != null && Math.max(...stackedData.datasets.map(o => o != null && Math.max(...o.data)));
    const stackedGridInterval = MIResults.getGridInterval(maxStackedDataPoint);
    let maxTrendDataPoint = 0;
    let trendData = null;
    let trendGridInterval = 5;
    if (trendAnalysisType !== 'none') {
      trendData = this.getTrendAnalysisData();
      maxTrendDataPoint = trendData != null && trendData.datasets != null && Math.max(...trendData.datasets.map(o => o != null && Math.max(...o.data)));
      trendGridInterval = MIResults.getGridInterval(maxTrendDataPoint);
    }

    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'right' } },
      onClick: (event, elem) => this.onPieChartSliceClick(event, elem)
    };

    if (filter.formType !== null) {
      pieOptions.legend = false;
    }

    const barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          onClick: (event, elems) => this.onStackedBarLegendClick(event, elems)
        },
      },
      scales: {
        x: {
          stacked: true,
          title: 'Form Types',
          font: {
            size: 14,
            style: 'bold'
          },
          ticks: {
            color: graphOptions.colors.axesLabels,
            callback(value, index) {
              const context = this;
              const label = typeof value === 'number' ? context.getLabelForValue(value) : value;
              return label.split(' ');
            },
          },
        },
        y: {
          stacked: true,
          title: '# Forms',
          ticks: {
            stepSize: stackedGridInterval,
            color: graphOptions.colors.axesLabels
          },
          font: {
            size: 14,
            style: 'bold'
          },
        }
      }
    };

    const trendOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          onClick: (event, elems) => this.onTrendBarLegendClick(event, elems)
        }
      },
      scales: {
        x: {
          stacked: true,
          title: 'Form Types',
          font: {
            size: 14,
            style: 'bold'
          },
          ticks: {
            color: graphOptions.colors.axesLabels,
            callback(value, index) {
              const context = this;
              const label = typeof value === 'number' ? context.getLabelForValue(value) : value;
              return [label.split(';')[0], label.split(';')[1]];
            },
          }
        },
        y: {
          stacked: true,
          title: '# Forms',
          font: {
            size: 14,
            style: 'bold'
          },
          ticks: {
            stepSize: trendGridInterval,
            fontColor: graphOptions.colors.axesLabels
          }
        }
      }
    };

    return (
      <div className="mi-dashboard mi-results">
        <div className="mi-toolbar">
          <button type="button" className="btn btn-link" onClick={ () => this.resetDashboard() }>
            &laquo; Back to Date Range Selection
          </button>
          { clients && clients.length > 0 && <div><h1 className="text-center">{ this.getSelectedClientName() } MI Report</h1>
            <p className="mt-2">Statistics may include draft Online forms but do not cover forms not yet checked for validation results. Forms not created in the given period will not appear in the metrics below.</p></div>}
          <AccessControl allowedPermissions={ [ActivityPerms.miDownload] }>
            <button type="button" className="btn float-right" onClick={ () => this.onDownloadExcelReportClick() }>
              { isGettingMiDashboardXls && <span className="icon loading" /> }
              Download Excel Report
            </button>
          </AccessControl>
        </div>
        <div className="top-level-stats">
          <div>
            <div><h4>Client Code</h4></div>
            <div className="heading-value"><div className="total-figure">{ data.clientCode }</div></div>
          </div>
          <div>
            <div><h4>From Validation Date</h4></div>
            <div className="heading-value"><div className="total-figure">{ moment(data.from).format('MMM DD, YYYY') }</div></div>
          </div>
          <div>
            <div><h4>To Validation Date</h4></div>
            <div className="heading-value"><div className="total-figure">{ moment(data.to).format('MMM DD, YYYY') }</div></div>
          </div>
        </div>
        <div className="top-level-stats">
          <div>
            <div><h4>All Forms Created</h4></div>
            <div className="heading-value"><div className="total-figure">{ data.total }</div></div>
          </div>
          <div>
            <div><h4>Forms Created then Expired</h4></div>
            <div className="heading-value"><div className="total-figure total-invalid-form-count">{ data.expired }</div></div>
          </div>
          <div>
            <div><h4>Valid (Not Expired) Complete Forms</h4></div>
            <div className="heading-value"><div className="total-figure total-valid-form-count">{ data.valid }</div></div>
          </div>
          <div>
            <div><h4>Invalid Complete Forms</h4></div>
            <div className="heading-value"><div className="total-figure total-invalid-form-count">{ data.invalid }</div></div>
          </div>
          <div>
            <div><h4>In Progress Forms</h4></div>
            <div className="heading-value"><div className="total-figure total-invalid-form-count">{ data.inProgress }</div></div>
          </div>
        </div>
        <div className="top-level-stats">
          <div>
            <div><h4>Online Forms</h4></div>
            <div className="heading-value"><div className="total-figure">{ data.onlineTotal }</div></div>
          </div>
          <div>
            <div><h4>Scanned Forms</h4></div>
            <div className="heading-value"><div className="total-figure">{ data.scannedTotal }</div></div>
          </div>
          <div>
            <div><h4>Digital Entry Forms</h4></div>
            <div className="heading-value"><div className="total-figure">{ data.digitalEntryTotal }</div></div>
          </div>
          <div>
            <div><h4>Batch Upload Forms</h4></div>
            <div className="heading-value"><div className="total-figure">{ data.batchTotal }</div></div>
          </div>
        </div>
        <div className="graph-results">
          <div className={ this.getChartHolderStyle('results-box') }>
            { filter.formType != null
              && <button type="button" className="btn btn-link undo-filter-link" onClick={ () => this.resetFilter() }>
                Undo filter
              </button> }
            <h4>All Forms by Form Type</h4>
            { !this.hasData() && <div className="no-results"><p>No Data to Show</p></div>}
            { this.hasData() && <div className={ this.getChartHolderStyle('chart-holder') }><Pie
              ref={ (c) => { this.pieChart = c; } }
              data={ this.getPieChartData() }
              options={ pieOptions }
            /></div> }
          </div>
          <div className={ this.getChartHolderStyle('results-box') }>
            <h4>
              Valid (Non Expired) vs Invalid/Expired Forms by Form Type
              <button id="hint-button" type="button" className={ `mi-hint-btn ${ showValidInvalidHint ? 'open' : '' }` } onClick={ () => this.setState({ showValidInvalidHint: !showValidInvalidHint }) }>
                <svg className="svg-icon">
                  <use xlinkHref="assets/images/hint-defs.svg#hint-icon" />
                </svg>
              </button>
              <div id="hint-panel" className={ !showValidInvalidHint ? 'hide' : '' }>
                <p>Forms validated against multiple account contexts are counted once; where a form is invalid for at least one account context, the form will be added to the invalid count.</p>
              </div>
            </h4>
            { !this.hasData() && <div className="no-results"><p>No Data to Show</p></div>}
            { this.hasData() && <div className={ this.getChartHolderStyle('chart-holder') }><Bar
              ref={ (c) => { this.stackedChart = c; } }
              data={ stackedData }
              options={ barOptions }
            /></div> }
          </div>
          { trendAnalysisType !== 'none' && <div className="results-box center">
            <h4>Trend Analysis</h4>
            { !this.hasTrendData() && <div className="no-results"><p>No Data to Show</p></div>}
            { this.hasTrendData() && <div className="chart-holder"><Bar
              ref={ (c) => { this.trendChart = c; } }
              data={ trendData }
              options={ trendOptions }
            /></div> }
            </div> }
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      settings: {
        clients,
      }
    },
    forms: {
      isMiDetailsModalShown
    }
  } = state;

  return {
    clients,
    isMiDetailsModalShown
  };
}
const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MIResults);

// export default MIResults;
